import React from 'react';

import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import ActiveFilters from 'components/base/ActiveFilters/ActiveFilters';

import Link from './Link';

import {
  Container,
  TitleBar,
  Title,
  TitleWrapper,
  HeaderLink,
  Filters,
  Content,
  FooterBar,
  HeaderLinkWrapper,
} from './shared.style';
import stringToSlug from 'helpers/stringToSlug';
import ToolbarOptions from 'components/utility/GenericComponent/ToolbarOptions/ToolbarOptions';
import { IToolbarItem } from 'components/utility/GenericComponent/GenericComponent';

/**
 * OverviewContainer is a container layout component for components specific to
 * the Overview topic on most entities. Use it to wrap Overview content.
 *
 * @param {object} props React props
 * @param {array=} props.filtersToShow An array of filter types to display, possible values are 'streaming', 'download', 'charts', 'country', 'media'
 * @param {any} props.children React children
 * @param {string} props.linkTo String or Object that gets passed on as "to" attribute of the Anchor element
 * @param {string=} props.hasAnchor Bool that defines if the link of this OverviewContainer is an anchor (links to something on the same page)
 * @param {string=} props.subtitle Block subtitle
 * @param {string} props.title Block title
 * @param {boolean=} props.short If the Container should not have margin top
 */

export type TFilterType =
  | 'charts'
  | 'country'
  | 'download'
  | 'streaming'
  | 'media'
  | 'customRegion'
  | 'region'
  | 'platform';
interface IOverviewContainerProps {
  linkTo: any;
  hasAnchor?: boolean;
  children: any;
  filtersToShow?: TFilterType[];
  subtitle?: string;
  title: string;
  short?: boolean;
  isNotPadded?: boolean;
  isMobile?: boolean;
  glossarySlug?: string;
  extraToolbarItems?: IToolbarItem[];
  linkTitle?: string;
  isTitleNotLink?: boolean;
  customizedMode?: {
    isShownCustomizeButton: boolean;
    onClick: () => void;
    customizeButtonText: string;
    isCustomizedActive: boolean;
  };
}

const OverviewContainer: React.FC<IOverviewContainerProps> = (props) => {
  const {
    filtersToShow,
    children,
    subtitle,
    title,
    hasAnchor,
    linkTo,
    short,
    isNotPadded,
    isMobile,
    glossarySlug,
    extraToolbarItems,
    linkTitle,
    isTitleNotLink,
  } = props;
  const navLabel = 'Navigate to more detailed version of this content';

  return (
    <Container data-pw={stringToSlug(title)} short={short}>
      <ErrorBoundary>
        <TitleBar isNotPadded={isNotPadded} isMobile={isMobile}>
          {subtitle ? (
            <TitleWrapper>
              <Title>
                <Link
                  isHeadline
                  title={title}
                  isAnchor={hasAnchor}
                  to={linkTo}
                />
              </Title>
              {subtitle && subtitle.length && <span>in {subtitle}</span>}
            </TitleWrapper>
          ) : (
            <Title isNotPadded={isNotPadded} isMobile={isMobile}>
              {isTitleNotLink ? (
                title
              ) : (
                <Link
                  isHeadline
                  title={title}
                  isAnchor={hasAnchor}
                  to={linkTo}
                />
              )}
            </Title>
          )}
          <HeaderLinkWrapper>
            <HeaderLink aria-label={navLabel}>
              <Link
                title={linkTitle || title}
                isAnchor={hasAnchor}
                to={linkTo}
              />
            </HeaderLink>
            {glossarySlug && (
              <ToolbarOptions
                glossarySlug={glossarySlug}
                extraToolbarItems={extraToolbarItems}
              />
            )}
          </HeaderLinkWrapper>
        </TitleBar>

        <Filters isNotPadded={isNotPadded} isMobile={isMobile}>
          <ActiveFilters allowedFilters={filtersToShow}></ActiveFilters>
        </Filters>
        <Content>{children}</Content>
        <FooterBar aria-label={navLabel}>
          <Link title={linkTitle || title} isAnchor={hasAnchor} to={linkTo} />
        </FooterBar>
      </ErrorBoundary>
    </Container>
  );
};

export default OverviewContainer;
